// src/pages/PrivacyPolicyPage.jsx
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, FileText, Database, Cookie, Link as LinkIcon, Clock, UserCheck, Mail } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Breadcrumbs from '../components/Shared/Breadcrumbs';

gsap.registerPlugin(ScrollTrigger);

const PrivacyPolicyPage = () => {
  const pageRef = useRef(null);

  const sections = [
    {
      id: 'information-collected',
      title: 'Information We Collect',
      icon: Database,
      color: 'from-blue-500 to-cyan-500',
      content: [
        {
          subtitle: 'Information You Provide Voluntarily',
          items: [
            'When you submit an inquiry, request samples or information, or contact us for partnerships or investment discussions',
            'You may provide: Name, Company name, Email address, Phone number, Business-related information'
          ]
        },
        {
          subtitle: 'Automatically Collected Information',
          items: [
            'When you browse our website, we may automatically collect: IP address, Browser type, Device information, Pages visited, Date and time of access',
            'This information is used for website performance, security, and analytics'
          ]
        }
      ]
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      icon: Eye,
      color: 'from-green-500 to-emerald-500',
      content: [
        'Respond to inquiries and requests',
        'Communicate regarding business opportunities',
        'Improve website functionality and user experience',
        'Maintain website security and integrity',
        'Comply with legal and regulatory obligations',
        'We do not sell, rent, or trade your personal information'
      ]
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing & Disclosure',
      icon: LinkIcon,
      color: 'from-purple-500 to-pink-500',
      content: [
        'With trusted service providers who assist in operating the website',
        'When required by law or regulatory authorities',
        'To protect our legal rights, safety, or property',
        'All third parties are required to maintain confidentiality and data security'
      ]
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: Lock,
      color: 'from-red-500 to-orange-500',
      content: [
        'We implement reasonable administrative, technical, and physical safeguards to protect your information',
        'Protections against unauthorized access, disclosure, alteration, or destruction',
        'While no digital system is completely secure, we strive to use industry-appropriate measures',
        'Regular security assessments and updates'
      ]
    },
    {
      id: 'cookies-analytics',
      title: 'Cookies & Analytics',
      icon: Cookie,
      color: 'from-yellow-500 to-amber-500',
      content: [
        'Our website may use cookies or similar technologies to:',
        '- Analyze website traffic',
        '- Understand visitor behavior',
        '- Improve site performance',
        'You may choose to disable cookies through your browser settings'
      ]
    },
    {
      id: 'third-party-links',
      title: 'Third-Party Links',
      icon: LinkIcon,
      color: 'from-indigo-500 to-blue-500',
      content: [
        'Our website may contain links to external websites',
        'We are not responsible for the privacy practices or content of third-party sites',
        'We encourage users to review the privacy policies of any external websites they visit'
      ]
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: Clock,
      color: 'from-teal-500 to-green-500',
      content: [
        'We retain personal information only for as long as necessary to:',
        '- Fulfill the purposes outlined in this policy',
        '- Comply with legal or regulatory requirements',
        '- Resolve disputes and enforce agreements'
      ]
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      icon: UserCheck,
      color: 'from-gold-500 to-yellow-500',
      content: [
        'Depending on applicable laws, you may have the right to:',
        '- Request access to your personal information',
        '- Request correction or deletion',
        '- Withdraw consent for communication',
        'Requests can be made by contacting us using the details below'
      ]
    },
    {
      id: 'policy-changes',
      title: 'Changes to This Privacy Policy',
      icon: FileText,
      color: 'from-gray-500 to-dark-600',
      content: [
        'We may update this Privacy Policy periodically',
        'Any changes will be posted on this page with an updated effective date',
        'We encourage you to review this policy regularly',
        'Continued use of the website constitutes acceptance of changes'
      ]
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate section headers
      gsap.fromTo('.section-header', {
        x: -50,
        opacity: 0,
      }, {
        x: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        scrollTrigger: {
          trigger: '.content-section',
          start: 'top 70%',
          toggleActions: 'play none none reverse',
        },
      });

      // Animate content cards
      gsap.fromTo('.content-card', {
        y: 50,
        opacity: 0,
      }, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        scrollTrigger: {
          trigger: '.content-card',
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });

      // Floating animation for icons
      gsap.to('.floating-icon', {
        y: -10,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        stagger: 0.3,
      });

      // Timeline for hero section
      const tl = gsap.timeline();
      tl.from('.privacy-hero-title', {
        y: 100,
        opacity: 0,
        duration: 1.2,
        ease: 'power3.out',
      })
      .from('.shield-icon', {
        scale: 0,
        rotation: 360,
        duration: 1,
        ease: 'back.out(1.7)',
      }, '-=0.5');
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div ref={pageRef} className="pt-20">
      <Breadcrumbs currentPage="Privacy Policy" />

      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-blue-900/20 to-dark-900" />
        <div className="absolute inset-0 glitter-bg" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="shield-icon inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 mb-6">
              <Shield className="w-10 h-10 md:w-12 md:h-12 text-white" />
            </div>
            
            <h1 className="privacy-hero-title text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent">
                Privacy Policy
              </span>
            </h1>
            
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-12 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500" />
              <p className="text-lg text-gray-300">
                Effective Date: {currentDate}
              </p>
              <div className="w-12 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500" />
            </div>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              We respect your privacy and are committed to protecting the personal information 
              you share with us. This Privacy Policy explains how information is collected, 
              used, disclosed, and safeguarded.
            </p>
            
            <div className="inline-flex items-center px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full">
              <span className="text-blue-400 text-sm">Last Updated: {currentDate}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      {/* <section className="py-8 bg-dark-800/30 border-y border-blue-500/20">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {sections.map((section) => (
              <a
                key={section.id}
                // href={`#${section.id}`}
                className="group inline-flex items-center px-4 py-2 rounded-full bg-dark-800/50 border border-blue-500/20 hover:border-blue-500/40 hover:bg-blue-500/10 transition-all duration-300"
              >
                <section.icon className="w-4 h-4 text-blue-400 mr-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm text-gray-300 group-hover:text-blue-300">
                  {section.title.split(' ')[0]}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section> */}

      {/* Main Content */}
      <section className="content-section py-16 md:py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            {/* Introduction */}
            <div className="content-card mb-12">
              <div className="bg-dark-900/50 rounded-2xl p-8 border border-blue-500/20">
                <p className="text-lg text-gray-300 mb-4">
                  By accessing or using this website, you agree to the terms outlined in this Privacy Policy.
                </p>
                <p className="text-gray-400">
                  This document outlines our commitment to transparency and your rights regarding personal data.
                </p>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-12">
              {sections.map((section, index) => (
                <div key={section.id} id={section.id} className="scroll-mt-24">
                  <div className="section-header mb-8">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className={`floating-icon w-12 h-12 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center`}>
                        <section.icon className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white">
                        {index + 1}. {section.title}
                      </h2>
                    </div>
                    <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" />
                  </div>

                  <div className="content-card">
                    <div className="bg-dark-800/30 rounded-xl border border-blue-500/10 p-6 md:p-8">
                      {Array.isArray(section.content[0]) || typeof section.content[0] === 'string' ? (
                        <ul className="space-y-4">
                          {section.content.map((item, idx) => (
                            <li key={idx} className="flex items-start">
                              <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3" />
                              <span className="text-gray-300">{item}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        section.content.map((subsection, subIdx) => (
                          <div key={subIdx} className="mb-6 last:mb-0">
                            <h3 className="text-lg font-semibold text-blue-400 mb-3">
                              {subsection.subtitle}
                            </h3>
                            <ul className="space-y-3 ml-4">
                              {subsection.items.map((item, itemIdx) => (
                                <li key={itemIdx} className="flex items-start">
                                  <div className="flex-shrink-0 w-1.5 h-1.5 bg-cyan-500 rounded-full mt-2 mr-3" />
                                  <span className="text-gray-300">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Section */}
            <div id="contact" className="mt-16 scroll-mt-24">
              <div className="section-header mb-8">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-500 to-yellow-500 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    Contact Information
                  </h2>
                </div>
                <div className="w-24 h-1 bg-gradient-to-r from-gold-500 to-yellow-500 rounded-full" />
              </div>

              <div className="content-card">
                <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-2xl border border-gold-500/20 p-8">
                  <p className="text-lg text-gray-300 mb-6">
                    If you have any questions regarding this Privacy Policy or how your data is handled, please contact us:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center space-x-4 p-4 bg-dark-900/50 rounded-xl border border-gold-500/20">
                      <div className="w-10 h-10 rounded-full bg-gold-500/20 border border-gold-500/30 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-gold-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Email</p>
                        <a href="mailto:info@profitmix.in" className="text-gold-300 hover:text-gold-200 transition-colors">
                          info@profitmix.in
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 p-4 bg-dark-900/50 rounded-xl border border-gold-500/20">
                      <div className="w-10 h-10 rounded-full bg-gold-500/20 border border-gold-500/30 flex items-center justify-center">
                        <span className="text-gold-400 text-lg">📍</span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Location</p>
                        <p className="text-gold-300">India</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-8 border-t border-gold-500/20">
                    <p className="text-center text-gray-400">
                      We typically respond to privacy-related inquiries within 48 hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-between">
              <Link
                to="/terms"
                className="px-6 py-3 border border-blue-500/30 text-blue-400 rounded-lg hover:bg-blue-500/10 transition-all duration-300 flex items-center justify-center"
              >
                ← View Terms of Service
              </Link>
              
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-500 hover:to-cyan-500 transition-all duration-300"
              >
                Back to Top ↑
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;